import { FC, useState, useEffect } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { SelectChangeEvent } from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import { Connection, ConnectionType } from "types";
import FortnoxConnection from "components/Forms/FortnoxConnectionForm";
import NextConnection from "components/Forms/NextConnectionForm";
import FileServerConnection from "components/Forms/FileServerConnectionForm";
import SDFConnection from "components/Forms/SDFConnectionForm";
import { useForm } from "react-hook-form";
import MeltanoConnection from "./MeltanoConnectionForm";

interface ConnectionFormData {
  connectionType: ConnectionType;
  payload: any;
  isValid: boolean;
}

interface Props {
  onChange: (value: ConnectionFormData) => void;
  clientId: string;
  initialData?: Connection;
  onFortnoxReauthorize?: () => Promise<void>;
}

export const ConnectionForm: FC<Props> = (props) => {
  const { onChange, clientId, initialData, onFortnoxReauthorize } = props;
  const { t } = useTranslation();
  const [connectionType, setConnectionType] = useState<ConnectionType>(
    initialData?.type ?? ConnectionType.Fortnox
  );

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false);

  const handleConnectionTypeChange = (event: SelectChangeEvent<unknown>) => {
    const newConnectionType = event.target.value as ConnectionType;
    setConnectionType(newConnectionType);
  };

  return (
    <Box>
      <FormControl fullWidth sx={{mt: 2}}>
        <InputLabel sx={{ml: -3.5}}>{initialData ? t("connection:connection_type") : t("connection:source")}</InputLabel>
        <Select
          IconComponent={() =>
            dropdownOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />
          }
          disabled={Boolean(initialData)}
          sx={{ color: "black", width: "60%" }}
          value={connectionType}
          onChange={handleConnectionTypeChange}
          variant={"standard"}
          onOpen={() => setDropdownOpen(true)}
          onClose={() => setDropdownOpen(false)}
        >
          <MenuItem value={ConnectionType.Fortnox}>
            {ConnectionType[ConnectionType.Fortnox]}
          </MenuItem>
          <MenuItem value={ConnectionType.Next}>
            {ConnectionType[ConnectionType.Next]}
          </MenuItem>
          <MenuItem value={ConnectionType.FileServer}>
            {ConnectionType[ConnectionType.FileServer]}
          </MenuItem>
          <MenuItem value={ConnectionType.SDF}>
            {ConnectionType[ConnectionType.SDF]}
          </MenuItem>
          <MenuItem value={ConnectionType.Meltano_Shopify}>
            {"Shopify"}
          </MenuItem>
        </Select>
      </FormControl>
      

      <Box mt={4}>
        {connectionType === ConnectionType.Fortnox && (
          <FortnoxConnection
            initialData={initialData}
            onReauthorize={onFortnoxReauthorize}
            onChange={(scopes) => {
              // Convert to the format expected by parent
              onChange({ connectionType, payload: scopes, isValid: true });
            }}
          />
        )}
        {connectionType === ConnectionType.Next && (
          <NextConnection
            onChange={(values) => {
              onChange({ connectionType, payload: values, isValid: values.isValid ?? false });
            }}
            initialData={initialData}
          />
        )}
        {connectionType === ConnectionType.FileServer && (
          <FileServerConnection
            onChange={(values) => {
              onChange({ 
                connectionType, 
                payload: {
                  credentials: values.credentials, 
                  paths: values.paths,
                  identifier: values.identifier,
                  format: values.format,
                  fileNameRegex: values.fileNameRegex
                },
                isValid: values.isValid ?? false
              });
            }}
            initialData={initialData}
          />
        )}
        {connectionType === ConnectionType.SDF && (
          <SDFConnection
            onChange={(values) => {
              onChange({ connectionType, payload: values, isValid: values.isValid ?? false });
            }}
            initialData={initialData}
          />
        )}
        {connectionType === ConnectionType.Meltano_Shopify && (
          <MeltanoConnection
            onChange={(values) => {
              onChange({ connectionType, payload: values, isValid: values.isValid ?? false });
            }}
            initialData={initialData}
          />
        )}
      </Box>
    </Box>
  );
};
